import {useEffect, useRef, useState} from 'react';
import {focusDeparture, focusDepartureDelay, shouldMonitorWindowBlur} from './evaluation-focus-policy';
import {attemptSessionId} from './activity-continuity';
import type {StudentAttempt} from './activity-library';
type Pending = {type:'focus_incident';attemptId:string;sessionId:string;generation:number;incidentId:string;reason:'hidden'|'blur'};
const keyFor=(id:string)=>`competenceo-focus-incident:${id}`;
function stored(id?:string):Pending|null {try{return id?JSON.parse(localStorage.getItem(keyFor(id))??'null'):null;}catch{return null;}}
export function useEvaluationFocus(attempt:StudentAttempt|undefined,enabled:boolean,onRefresh?:()=>Promise<void>) {
 const [pending,setPending]=useState<Pending|null>(()=>stored(attempt?.id));
 const [error,setError]=useState('');
 const pendingRef=useRef(pending);pendingRef.current=pending;
 const current=useRef({attempt,enabled,onRefresh});current.current={attempt,enabled,onRefresh};
 const allowedUntil=useRef(0);
 const allowExternal=()=>{allowedUntil.current=Date.now()+120000;};
 const clear=()=>{if(attempt)try{localStorage.removeItem(keyFor(attempt.id));}catch{}pendingRef.current=null;setPending(null);setError('');};
 useEffect(()=>{
  if(!pending || !attempt)return;
  if(attempt.focusGuard?.blocked || (attempt.focusGuard?.generation??0)>pending.generation || attemptSessionId(attempt)!==pending.sessionId || attempt.status!=='in_progress')clear();
 },[attempt?.revision]);
 useEffect(()=>{
  if(!pending||!enabled)return;
  let stopped=false,sending=false;
  async function send(){
   if(sending)return;sending=true;
   try {
    const response=await fetch('/api/activity-library',{method:'POST',credentials:'include',keepalive:true,headers:{'Content-Type':'application/json'},body:JSON.stringify(pending)});
    if(!response.ok)throw new Error('Alerte non transmise. Activité bloquée localement ; nouvelle tentative automatique.');
    await current.current.onRefresh?.();
    if(!stopped)setError('');
   }catch(e){if(!stopped)setError(e instanceof Error?e.message:'Connexion interrompue.');}finally{sending=false;}
  }
  void send();const timer=setInterval(()=>void send(),3000);
  return()=>{stopped=true;clearInterval(timer);};
 },[pending,enabled]);
 useEffect(()=>{
  if(!enabled)return;
  let hiddenTimer:ReturnType<typeof setTimeout>|undefined;
  let blurTimer:ReturnType<typeof setTimeout>|undefined;

  function report(reason:'hidden'|'blur'){
   const a=current.current.attempt;
   if(!current.current.enabled||!a||a.focusGuard?.blocked||pendingRef.current||Date.now()<allowedUntil.current)return;
   const sessionId=attemptSessionId(a);if(!sessionId)return;
   const event:Pending={type:'focus_incident',attemptId:a.id,sessionId,generation:a.focusGuard?.generation??0,incidentId:crypto.randomUUID(),reason};
   pendingRef.current=event;
   try{localStorage.setItem(keyFor(a.id),JSON.stringify(event));}catch{}
   setPending(event);
  }

  function embeddedFrameFocused(){
   const active=document.activeElement;
   return active?.tagName==='IFRAME' && !!active.closest('.library-work');
  }

  const monitorBlur=shouldMonitorWindowBlur(
   window.matchMedia?.('(pointer: coarse)').matches??false,
   navigator.maxTouchPoints??0,
  );

  function cancelHidden(){if(hiddenTimer){clearTimeout(hiddenTimer);hiddenTimer=undefined;}}
  function cancelBlur(){if(blurTimer){clearTimeout(blurTimer);blurTimer=undefined;}}

  function schedule(reason:'hidden'|'blur'){
   if(Date.now()<allowedUntil.current||pendingRef.current)return;
   if(reason==='hidden'){
    if(hiddenTimer)return;
    hiddenTimer=setTimeout(()=>{
     hiddenTimer=undefined;
     if(document.visibilityState==='hidden'&&Date.now()>=allowedUntil.current)report('hidden');
    },focusDepartureDelay('hidden'));
    return;
   }
   if(blurTimer)return;
   blurTimer=setTimeout(()=>{
    blurTimer=undefined;
    const departure=focusDeparture(document.visibilityState,document.hasFocus(),embeddedFrameFocused());
    if(departure==='blur'&&Date.now()>=allowedUntil.current)report('blur');
   },focusDepartureDelay('blur'));
  }

  const visibility=()=>{
   if(document.visibilityState==='hidden')schedule('hidden');
   else cancelHidden();
  };
  const blur=()=>{if(monitorBlur)schedule('blur');};
  const focus=()=>{
   cancelBlur();
   cancelHidden();
   if(allowedUntil.current)allowedUntil.current=Math.min(allowedUntil.current,Date.now()+500);
  };
  const tick=setInterval(()=>{
   if(Date.now()<allowedUntil.current)return;
   const departure=focusDeparture(document.visibilityState,document.hasFocus(),embeddedFrameFocused());
   if(departure==='hidden') schedule('hidden');
   else if(departure==='blur'&&monitorBlur) schedule('blur');
   else {cancelHidden();cancelBlur();}
  },1000);

  window.addEventListener('blur',blur);
  window.addEventListener('focus',focus);
  document.addEventListener('visibilitychange',visibility);
  return()=>{
   clearInterval(tick);
   cancelHidden();
   cancelBlur();
   window.removeEventListener('blur',blur);
   window.removeEventListener('focus',focus);
   document.removeEventListener('visibilitychange',visibility);
  };
 },[enabled,attempt?.id]);
 return {blocked:enabled&&(!!pending||!!attempt?.focusGuard?.blocked),error,allowExternal};
}
